/* ==========================================================================
   Blue Sky Cashmere B2B — Theme JavaScript
   ========================================================================== */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     bfcache state reset (2026-05-16 bug-sweep)
     -------------------------------------------------------------------------- *
     Chrome's back/forward cache restores pages with their full state intact —
     including drawers that were open when the user navigated away. On rapid
     back/forward navigation this surfaces as a millisecond flash of the
     restored open drawer / overlay before the user's eyes catch up. Listen
     for the pageshow `persisted` flag and force-close every modal surface
     when the page is restored from cache.
     -------------------------------------------------------------------------- */
  window.addEventListener('pageshow', function(e) {
    if (!e.persisted) return;
    document.querySelectorAll(
      '[data-menu-drawer], [data-sample-cart-drawer], [data-search-drawer], .floating-contact.is-open, [data-lang-switcher].is-open, [data-nav-item].is-active, [data-accordion-item].is-open, [data-mobile-accordion-item].is-open, .site-footer__column.is-open'
    ).forEach(function(el) { el.classList.remove('is-open'); el.classList.remove('is-active'); });
    document.querySelectorAll(
      '[data-menu-overlay], [data-sample-cart-overlay]'
    ).forEach(function(el) { el.classList.remove('is-active'); });
    document.body.style.overflow = '';
  });

  /* --------------------------------------------------------------------------
     Header — Sticky scroll behavior
     -------------------------------------------------------------------------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    let lastScroll = 0;
    const scrollThreshold = 10;

    window.addEventListener('scroll', function() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* --------------------------------------------------------------------------
     Mobile Menu Drawer
     -------------------------------------------------------------------------- */
  const menuTrigger = document.querySelector('[data-menu-trigger]');
  const menuDrawer = document.querySelector('[data-menu-drawer]');
  const menuClose = document.querySelector('[data-menu-close]');
  const menuOverlay = document.querySelector('[data-menu-overlay]');

  function openMenu() {
    if (!menuDrawer) return;
    menuDrawer.classList.add('is-open');
    if (menuOverlay) menuOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    menuTrigger?.setAttribute('aria-expanded', 'true');
  }

  function closeMenu() {
    if (!menuDrawer) return;
    menuDrawer.classList.remove('is-open');
    if (menuOverlay) menuOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
    menuTrigger?.setAttribute('aria-expanded', 'false');
  }

  menuTrigger?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);

  /* --------------------------------------------------------------------------
     Mobile Menu Accordions
     -------------------------------------------------------------------------- */
  /* CSS-driven accordion (Phase 5): only toggle .is-open + ARIA — height
     is animated via grid-template-rows: 0fr → 1fr in CSS. */
  document.querySelectorAll('[data-mobile-accordion-trigger]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      const item = this.closest('[data-mobile-accordion-item]');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      // Close all other items (single-open accordion)
      document.querySelectorAll('[data-mobile-accordion-item].is-open').forEach(function(openItem) {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          const otherTrigger = openItem.querySelector('[data-mobile-accordion-trigger]');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.toggle('is-open', !isOpen);
      this.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });

  /* --------------------------------------------------------------------------
     Desktop Mega Menu / Dropdowns
     -------------------------------------------------------------------------- */
  document.querySelectorAll('[data-mega-trigger]').forEach(function(trigger) {
    const parent = trigger.closest('[data-nav-item]');
    const dropdown = parent?.querySelector('[data-mega-content]');
    if (!parent || !dropdown) return;

    let hideTimeout;

    // POST-BUG-SWEEP 2026-05-16 — aria-expanded was initialized in markup but
    // never toggled here, leaving screen-reader users stuck on "false" even
    // when the dropdown is visible. Mirror the is-active class state.
    function setOpen(open) {
      if (open) {
        parent.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
      } else {
        parent.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      }
    }

    parent.addEventListener('mouseenter', function() {
      clearTimeout(hideTimeout);
      // Close other open menus
      document.querySelectorAll('[data-nav-item].is-active').forEach(function(item) {
        if (item !== parent) {
          item.classList.remove('is-active');
          item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
        }
      });
      setOpen(true);
    });

    parent.addEventListener('mouseleave', function() {
      // POST-CUSTOMER-AUDIT 2026-05-21 — was 200ms; produced visible
      // "two dropdowns open" overlap when moving between top-level items.
      // 80ms preserves the grace period for diagonal mouse paths.
      hideTimeout = setTimeout(function() {
        setOpen(false);
      }, 80);
    });

    // Keyboard accessibility
    trigger.addEventListener('focus', function() {
      clearTimeout(hideTimeout);
      setOpen(true);
    });

    dropdown.addEventListener('focusout', function(e) {
      if (!parent.contains(e.relatedTarget)) {
        setOpen(false);
      }
    });
  });

  /* --------------------------------------------------------------------------
     Accordion (general purpose)
     -------------------------------------------------------------------------- */
  /* CSS-driven accordion (Phase 5): toggle class only, height handled by CSS. */
  document.querySelectorAll('[data-accordion-trigger]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      const item = this.closest('[data-accordion-item]');
      if (!item) return;
      const isOpen = item.classList.toggle('is-open');
      this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  /* --------------------------------------------------------------------------
     Announcement Bar Rotation
     -------------------------------------------------------------------------- */
  const announcementSlides = document.querySelectorAll('[data-announcement-slide]');
  if (announcementSlides.length > 1) {
    let currentSlide = 0;
    setInterval(function() {
      announcementSlides[currentSlide].classList.remove('is-active');
      currentSlide = (currentSlide + 1) % announcementSlides.length;
      announcementSlides[currentSlide].classList.add('is-active');
    }, 5000);
  }

  /* --------------------------------------------------------------------------
     Smooth Scroll for Anchor Links
     -------------------------------------------------------------------------- */
  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();
      const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 65;
      const announcementOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')) || 36;
      const offset = headerOffset + announcementOffset + 20;

      window.scrollTo({
        top: target.offsetTop - offset,
        behavior: 'smooth'
      });
    });
  });

  /* --------------------------------------------------------------------------
     Form Validation
     -------------------------------------------------------------------------- */
  /* PHASE-PRE-LAUNCH RE-WORK: original implementation attached `submit` listener
     to the per-form element in BUBBLE phase. The Shopify hCaptcha integration
     and the auto-injected "contact form" wrappers intercept submit events
     before they bubble back up — so the per-form bubble handler never fired
     (verified via Cowork DOM inspection: bscValidateBound was set on the form
     but no validation banner, error class, or aria-invalid attributes ever
     appeared). Switching to:
       (a) Document-level submit listener in CAPTURE phase — runs before any
           wrapper/captcha integration can stopPropagation.
       (b) Eager `novalidate` set on every form with [data-validate-form]
           markers so the native HTML5 popup never fires.
       (c) Re-applied on MutationObserver in case any wrapper script clones the
           form into a different DOM node. */

  function _bscFormFromTarget(target) {
    if (!target) return null;
    if (target.tagName === 'FORM') return target;
    if (target.matches && target.matches('[data-validate-form]')) {
      return target.tagName === 'FORM' ? target : target.closest('form');
    }
    const marker = target.querySelector && target.querySelector('[data-validate-form]');
    return marker ? (marker.tagName === 'FORM' ? marker : marker.closest('form')) : null;
  }

  function _bscIsValidatedForm(form) {
    if (!form) return false;
    if (form.dataset.validateForm !== undefined) return true; // form has the attr
    return !!form.querySelector('[data-validate-form]');       // button inside has the attr
  }

  function _bscApplyNovalidate() {
    document.querySelectorAll('[data-validate-form]').forEach(function(marker) {
      const form = marker.tagName === 'FORM' ? marker : marker.closest('form');
      if (form && !form.hasAttribute('novalidate')) {
        form.setAttribute('novalidate', 'novalidate');
        form.dataset.bscValidateBound = '1';
      }
    });
  }

  // Run immediately + on DOMContentLoaded + on subsequent DOM mutations to
  // survive any later wrapper scripts that clone or re-render the form.
  _bscApplyNovalidate();
  document.addEventListener('DOMContentLoaded', _bscApplyNovalidate);
  if ('MutationObserver' in window) {
    new MutationObserver(_bscApplyNovalidate).observe(document.documentElement, {
      childList: true, subtree: true
    });
  }

  /* PHASE-PRE-LAUNCH ROUND-3: bind on document `click` in capture phase (not
     `submit`) — Shopify auto-injects hCaptcha for {% form 'contact' %} forms,
     and on the FIRST click, hCaptcha eats the submit event entirely to run a
     challenge. Listening for `click` catches the user's intent EARLIER, before
     hCaptcha can intercept. We then validate, and if any required field is
     empty, preventDefault on the click — which stops the button from triggering
     hCaptcha at all. */
  function _bscRunValidation(e, btn) {
    const form = _bscFormFromTarget(btn);
    if (!_bscIsValidatedForm(form)) return true; // not one of our forms — let it through

    let isValid = true;
    const invalidFields = [];

      /* PHASE-PRE-LAUNCH: aggregate multi-checkbox groups into a single hidden
         input. data-multi-source="X" wraps the checkboxes; the corresponding
         data-multi-target="X" hidden input receives the comma-joined values. */
      form.querySelectorAll('[data-multi-source]').forEach(function(group) {
        const key = group.getAttribute('data-multi-source');
        const target = form.querySelector('input[data-multi-target="' + key + '"]');
        if (!target) return;
        const values = Array.from(group.querySelectorAll('[data-multi-checkbox]:checked'))
          .map(function(cb) { return cb.value; });
        target.value = values.join(', ');
      });

      // Re-enable any fields we'd previously disabled on a failed-validation attempt
      // so the user can fill them in this round.
      form.querySelectorAll('[data-was-empty]').forEach(function(f) {
        f.disabled = false;
        f.removeAttribute('data-was-empty');
      });

      // Clear previous errors
      form.querySelectorAll('.form-error').forEach(function(err) { err.remove(); });
      form.querySelectorAll('.is-error').forEach(function(el) { el.classList.remove('is-error'); });
      form.querySelectorAll('[data-form-error-banner]').forEach(function(b) { b.remove(); });

      function fieldLabel(field) {
        if (field.id) {
          const lbl = form.querySelector('label[for="' + field.id + '"]');
          if (lbl) return lbl.textContent.replace(/[*\s]+$/, '').trim();
        }
        return field.getAttribute('aria-label') || field.name || 'This field';
      }

      function flagField(field, message) {
        isValid = false;
        invalidFields.push({ field: field, message: message });
        field.classList.add('is-error');
        field.setAttribute('aria-invalid', 'true');
        const error = document.createElement('div');
        error.className = 'form-error';
        error.textContent = message;
        // Insert after the field wrapper for predictable visual placement
        const insertionParent = field.closest('.form-group') || field.parentNode;
        insertionParent.appendChild(error);

        /* PHASE-PRE-LAUNCH ROUND-4: auto-clear the per-field error as soon as
           the user starts typing/fixing the field. Bind once per error; the
           handler removes itself after firing. */
        const clearOnFix = function() {
          field.classList.remove('is-error');
          field.removeAttribute('aria-invalid');
          if (error.parentNode) error.parentNode.removeChild(error);
          field.removeEventListener('input', clearOnFix);
          field.removeEventListener('change', clearOnFix);
        };
        field.addEventListener('input', clearOnFix);
        field.addEventListener('change', clearOnFix); // covers select / checkbox
      }

      // Validate required fields
      form.querySelectorAll('[required]').forEach(function(field) {
        const val = (field.value || '').trim();
        if (field.type === 'checkbox' && !field.checked) {
          flagField(field, fieldLabel(field) + ' is required');
        } else if (!val) {
          flagField(field, fieldLabel(field) + ' is required');
        }
      });

      // Validate email fields
      form.querySelectorAll('input[type="email"]').forEach(function(field) {
        if (field.classList.contains('is-error')) return; // already flagged as empty
        if (field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
          flagField(field, 'Please enter a valid email address');
        }
      });

      if (!isValid) {
        e.preventDefault();
        e.stopImmediatePropagation();

        /* Defensive cleanup: another submit handler (Shopify wrapper / hCaptcha)
           may have added the spinner / submitting class before we fired in
           capture phase. Strip it so the UI doesn't lie about success. */
        form.classList.remove('form-submitting');
        form.querySelectorAll('.is-loading').forEach(function(el) {
          el.classList.remove('is-loading');
          el.disabled = false;
        });

        /* PHASE-PRE-LAUNCH ROUND-4: clear any previous toast before adding a
           new one. Without this, repeated submits stack toasts on top of each
           other (Cowork round-3 finding). Remove from anywhere in the DOM. */
        document.querySelectorAll('[data-form-error-banner]').forEach(function(prev) {
          if (prev.parentNode) prev.parentNode.removeChild(prev);
        });

        /* PHASE-PRE-LAUNCH ROUND-3: render the error banner as position:FIXED
           (toast-style) so it's visible regardless of the user's scroll
           position. Avoids the scrollIntoView race conditions we hit earlier. */
        const banner = document.createElement('div');
        banner.className = 'form-message--error form-message--error-toast';
        banner.setAttribute('data-form-error-banner', '');
        banner.setAttribute('role', 'alert');
        banner.setAttribute('tabindex', '-1');
        const title = document.createElement('p');
        title.className = 'form-message__title';
        title.textContent = invalidFields.length === 1
          ? 'Please complete this required field:'
          : 'Please complete ' + invalidFields.length + ' required fields:';
        banner.appendChild(title);
        const list = document.createElement('ul');
        invalidFields.forEach(function(f) {
          const li = document.createElement('li');
          li.textContent = f.message;
          list.appendChild(li);
        });
        banner.appendChild(list);
        // Append to BODY so position:fixed isn't constrained by a transformed ancestor
        document.body.appendChild(banner);

        // Auto-focus banner for screen readers + give it a slight reveal animation
        requestAnimationFrame(function() {
          banner.classList.add('is-visible');
          banner.focus({ preventScroll: true });
        });

        // Banner persists until the user re-submits (next round will clear it).
        // Add a close button so users can dismiss it manually.
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'form-message__close';
        closeBtn.setAttribute('aria-label', 'Dismiss error');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', function() {
          if (banner.parentNode) banner.parentNode.removeChild(banner);
        });
        banner.appendChild(closeBtn);

        return false;
      }

      /* PHASE-PRE-LAUNCH: disable empty OPTIONAL fields so they don't appear
         as blank lines in the admin notification email ("VAT Number:" with no
         value, etc). Disabled inputs are excluded from form submission. Done
         AFTER validation so the user is never blocked from filling required
         ones on a re-attempt. */
      form.querySelectorAll('input[name^="contact["]:not([required]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea[name^="contact["]:not([required]), select[name^="contact["]:not([required])').forEach(function(field) {
        if (!field.value || !field.value.trim()) {
          field.disabled = true;
          field.setAttribute('data-was-empty', '1');
        }
      });
      // Hidden aggregator fields: drop if no boxes were checked.
      form.querySelectorAll('input[data-multi-target]').forEach(function(field) {
        if (!field.value) {
          field.disabled = true;
          field.setAttribute('data-was-empty', '1');
        }
      });

      /* PHASE-POST-LAUNCH-FIX (2026-05-28): visual loading state ONLY — do NOT
         disable the button here. Setting `disabled = true` during the click
         event chain (in capture phase) prevents the browser from triggering
         the form's default submit action — the click fires, but the disabled
         state causes the browser to skip the implicit form-submit step that
         would normally follow. Result: form hangs forever in loading state
         with no POST fired (Cowork reproduced this for Quote + Samples on
         2026-05-28). Stick with `is-loading` class for visual feedback;
         the recovery timeout below will catch genuinely-stuck submissions.
         If a double-click protection is needed, do it on the SUBMIT event
         (bubble phase), AFTER the browser has dispatched the form-submit. */
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        const originalText = submitBtn.textContent;
        submitBtn.dataset.originalText = originalText;
      }
      form.classList.add('form-submitting');

      /* Defer the actual disabled state until AFTER the browser dispatches
         the form-submit event. Listening once on the form's bubble-phase
         submit, we set disabled = true so a second click is impossible.
         This runs AFTER the browser's default-action has already been
         scheduled, so it can't cancel submission. */
      if (submitBtn && !submitBtn.dataset.bscDeferredDisableBound) {
        submitBtn.dataset.bscDeferredDisableBound = '1';
        form.addEventListener('submit', function _bscDeferredDisable() {
          // Use setTimeout to set disabled AFTER the submit handler chain has
          // released control back to the browser's submission machinery.
          setTimeout(function() { submitBtn.disabled = true; }, 0);
          form.removeEventListener('submit', _bscDeferredDisable);
        }, false);
      }

      /* PHASE-PRE-LAUNCH ROUND-5: submission-hang recovery — REVISED. Previous
         15s timeout was too aggressive: hCaptcha challenges legitimately take
         5-20 seconds to solve (the user clicking through "select all buses" UI),
         so the toast was firing DURING valid submissions and confusing users.
         New behavior:
           - 45 second base timeout (up from 15s)
           - Before firing, check if an hCaptcha challenge iframe is visible;
             if so, the user is mid-challenge — wait another 30s and re-check
           - Only fire if form is still stuck AND no captcha challenge is up */
      if (form.dataset.bscRecoveryTimer) {
        clearTimeout(parseInt(form.dataset.bscRecoveryTimer, 10));
      }
      function _bscMaybeFireRecovery() {
        if (!form.classList.contains('form-submitting')) return;
        // Skip if hCaptcha is showing a challenge — the user is interacting
        const captchaVisible = Array.from(document.querySelectorAll('iframe[src*="hcaptcha.com"]'))
          .some(function(iframe) {
            const r = iframe.getBoundingClientRect();
            return r.width > 50 && r.height > 50;
          });
        if (captchaVisible) {
          // User is solving captcha; check again in 30s
          const retry = setTimeout(_bscMaybeFireRecovery, 30000);
          form.dataset.bscRecoveryTimer = String(retry);
          return;
        }
        form.classList.remove('form-submitting');
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
        }
        // Show a recovery toast — same toast component used for validation.
        document.querySelectorAll('[data-form-error-banner]').forEach(function(prev) {
          if (prev.parentNode) prev.parentNode.removeChild(prev);
        });
        const banner = document.createElement('div');
        banner.className = 'form-message--error form-message--error-toast';
        banner.setAttribute('data-form-error-banner', '');
        banner.setAttribute('role', 'alert');
        banner.setAttribute('tabindex', '-1');
        const title = document.createElement('p');
        title.className = 'form-message__title';
        title.textContent = 'We didn\'t hear back from the server.';
        banner.appendChild(title);
        const msg = document.createElement('p');
        msg.style.margin = '8px 0 0';
        msg.style.fontSize = '13px';
        msg.textContent = 'This sometimes happens after a few failed attempts. Wait a minute and try once more, or email us directly at b2b@blueskycashmere.trade.';
        banner.appendChild(msg);
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'form-message__close';
        closeBtn.setAttribute('aria-label', 'Dismiss');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', function() {
          if (banner.parentNode) banner.parentNode.removeChild(banner);
        });
        banner.appendChild(closeBtn);
        document.body.appendChild(banner);
        requestAnimationFrame(function() { banner.classList.add('is-visible'); banner.focus({ preventScroll: true }); });
      }
      const timerId = setTimeout(_bscMaybeFireRecovery, 45000);
      form.dataset.bscRecoveryTimer = String(timerId);
      return true;
  } /* end _bscRunValidation */

  /* Click-phase intercept: runs BEFORE hCaptcha can swallow the first click. */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('button[type="submit"], input[type="submit"]');
    if (!btn) return;
    const form = _bscFormFromTarget(btn);
    if (!_bscIsValidatedForm(form)) return;
    const passed = _bscRunValidation(e, btn);
    if (!passed) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  /* Submit-phase intercept (capture): fallback for keyboard Enter / form.submit() */
  document.addEventListener('submit', function(e) {
    const form = _bscFormFromTarget(e.target);
    if (!_bscIsValidatedForm(form)) return;
    const submitter = e.submitter || form.querySelector('button[type="submit"], input[type="submit"]');
    if (form.dataset.bscRoundDone === '1') {
      form.dataset.bscRoundDone = '';
      return; // already validated this round (via click handler); let submit through
    }
    const passed = _bscRunValidation(e, submitter);
    if (!passed) {
      e.preventDefault();
      e.stopImmediatePropagation();
    } else {
      form.dataset.bscRoundDone = '1';
    }
  }, true); /* CAPTURE phase: runs before any hCaptcha / Shopify wrapper can intercept */

  /* --------------------------------------------------------------------------
     Search drawer
     -------------------------------------------------------------------------- */
  const searchTrigger = document.querySelector('[data-search-trigger]');
  const searchDrawer = document.querySelector('[data-search-drawer]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInput = document.querySelector('[data-search-input]');

  function openSearch() {
    if (!searchDrawer) return;
    searchDrawer.classList.add('is-open');
    searchTrigger?.setAttribute('aria-expanded', 'true');
    setTimeout(function() { searchInput?.focus(); }, 100);
  }
  function closeSearch() {
    if (!searchDrawer) return;
    searchDrawer.classList.remove('is-open');
    searchTrigger?.setAttribute('aria-expanded', 'false');
  }
  searchTrigger?.addEventListener('click', function() {
    searchDrawer.classList.contains('is-open') ? closeSearch() : openSearch();
  });
  searchClose?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && searchDrawer?.classList.contains('is-open')) closeSearch();
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      searchDrawer?.classList.contains('is-open') ? closeSearch() : openSearch();
    }
  });
  document.addEventListener('click', function(e) {
    if (!searchDrawer?.classList.contains('is-open')) return;
    if (searchDrawer.contains(e.target) || searchTrigger?.contains(e.target)) return;
    closeSearch();
  });

  /* --------------------------------------------------------------------------
     Language preference (30-day cookie)
     Shopify only persists locale via URL prefix or session cookie. Without
     subfolder URLs configured in Markets, locale is forgotten on navigation.
     We layer a 30-day cookie that:
       1. Captures the user's choice when they pick a language
       2. On any page load where Shopify's current locale doesn't match the
          cookie, silently submits a /localization form to switch back
     -------------------------------------------------------------------------- */
  (function() {
    const COOKIE_NAME = 'bsc_lang';
    const COOKIE_DAYS = 30;
    const REDIRECT_KEY = 'bsc_lang_redirect_path';

    function setLangCookie(code) {
      const expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();
      document.cookie = COOKIE_NAME + '=' + encodeURIComponent(code) +
        '; path=/; expires=' + expires + '; SameSite=Lax';
    }
    function getLangCookie() {
      const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]+)'));
      return m ? decodeURIComponent(m[1]) : null;
    }

    // Capture clicks on any locale_code submit button (header switcher + mobile drawer)
    document.querySelectorAll('button[name="locale_code"]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.value) setLangCookie(btn.value);
      });
    });

    // Restore preferred language on load if Shopify rendered a different one
    const preferred = getLangCookie();
    if (!preferred) return;
    const current = (document.documentElement.lang || '').toLowerCase().split('-')[0];
    // Compare PRIMARY subtags on both sides. The cookie stores the full locale
    // code (e.g. "zh-CN") while <html lang> may differ in region; comparing only
    // the full strings made region-tagged locales (zh-CN) never match → a redirect
    // fired on every load even when already on the correct locale (audit flash bug).
    const preferredLang = preferred.toLowerCase().split('-')[0];
    if (!current || preferredLang === current) return;
    // Avoid redirect loops on the same path
    if (sessionStorage.getItem(REDIRECT_KEY) === window.location.pathname) return;

    // Find Shopify's own localization form (already on the page via {% form 'localization' %})
    // — it has the form_type, utf8, and any CSRF metadata Shopify expects. Building a form
    // from scratch lands at /localization with an error.
    const targetBtn = document.querySelector(
      'form[action*="/localization"] button[name="locale_code"][value="' + preferred + '"]'
    );
    if (!targetBtn) return; // language not available on this storefront — silently bail
    sessionStorage.setItem(REDIRECT_KEY, window.location.pathname);
    const form = targetBtn.closest('form');
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(targetBtn); // proper submitter — sends locale_code=preferred
    } else {
      targetBtn.click(); // fallback for older browsers
    }
  })();

  /* --------------------------------------------------------------------------
     Language switcher dropdown
     -------------------------------------------------------------------------- */
  const langSwitcher = document.querySelector('[data-lang-switcher]');
  if (langSwitcher) {
    const langTrigger = langSwitcher.querySelector('[data-lang-trigger]');
    const closeLang = function() {
      langSwitcher.classList.remove('is-open');
      langTrigger?.setAttribute('aria-expanded', 'false');
    };
    langTrigger?.addEventListener('click', function(e) {
      e.stopPropagation();
      const isOpen = langSwitcher.classList.toggle('is-open');
      langTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    document.addEventListener('click', function(e) {
      if (!langSwitcher.classList.contains('is-open')) return;
      if (langSwitcher.contains(e.target)) return;
      closeLang();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && langSwitcher.classList.contains('is-open')) {
        closeLang();
        langTrigger?.focus();
      }
    });
  }

  /* --------------------------------------------------------------------------
     Floating contact widget — hides when footer is in view so it doesn't cover
     the social icons / copyright row. (Sticky CTA bar deleted PHASE-32; the
     scroll-threshold show/hide pattern was removed with it.)
     -------------------------------------------------------------------------- */
  const floatingContact = document.querySelector('[data-floating-contact]');
  if (floatingContact) {
    const footer = document.querySelector('.site-footer');
    if (footer && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(function(entries) {
        const footerVisible = entries[0].isIntersecting;
        floatingContact.classList.toggle('is-hidden-near-footer', footerVisible);
      }, { rootMargin: '0px 0px -120px 0px' });
      io.observe(footer);
    }
  }

  /* --------------------------------------------------------------------------
     Back to top (audit FN-04)
     -------------------------------------------------------------------------- */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    const prefersReducedBtt = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const toggleBackToTop = function() {
      backToTop.classList.toggle('is-visible', window.pageYOffset > 600);
    };
    toggleBackToTop();
    window.addEventListener('scroll', toggleBackToTop, { passive: true });
    backToTop.addEventListener('click', function() {
      window.scrollTo({ top: 0, behavior: prefersReducedBtt ? 'auto' : 'smooth' });
    });
  }

  /* --------------------------------------------------------------------------
     Tabs component
     -------------------------------------------------------------------------- */
  document.querySelectorAll('[data-tabs]').forEach(function(container) {
    const tabs = container.querySelectorAll('[data-tab]');
    const panels = container.querySelectorAll('[data-tab-panel]');

    function activate(key) {
      tabs.forEach(function(t) {
        const match = t.dataset.tab === key;
        t.setAttribute('aria-selected', match ? 'true' : 'false');
        t.tabIndex = match ? 0 : -1;
      });
      panels.forEach(function(p) {
        p.classList.toggle('is-active', p.dataset.tabPanel === key);
      });
    }

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() { activate(this.dataset.tab); });
      tab.addEventListener('keydown', function(e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const list = Array.from(tabs);
        const idx = list.indexOf(this);
        const next = e.key === 'ArrowRight'
          ? list[(idx + 1) % list.length]
          : list[(idx - 1 + list.length) % list.length];
        next.focus();
        activate(next.dataset.tab);
      });
    });

    // Activate first by default
    if (tabs.length) activate(tabs[0].dataset.tab);
  });

  /* --------------------------------------------------------------------------
     Scroll Reveal Animations
     -------------------------------------------------------------------------- */
  const revealElements = document.querySelectorAll('[data-reveal]');
  if (revealElements.length > 0 && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 200px 0px' }); /* PHASE-69 / 6.13: fire observer earlier — threshold 0.1→0 + rootMargin -40px→+200px so reveal completes BEFORE element enters viewport, eliminating blank-cream-during-scroll on Safari (browsers without animation-timeline) */

    revealElements.forEach(function(el) {
      revealObserver.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     Footer Accordion (Mobile)
     -------------------------------------------------------------------------- */
  /* CSS-driven footer accordion (Phase 5): toggle class only. */
  document.querySelectorAll('[data-footer-accordion]').forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      if (window.innerWidth > 768) return;
      const column = trigger.closest('[data-footer-column]');
      if (column) column.classList.toggle('is-open');
    });
  });

  /* --------------------------------------------------------------------------
     Animated stat counters — Wave I
     --------------------------------------------------------------------------
     Numbers in `.stat__number`, `.prg-stat__number`, `.team-stat__number`,
     and any element with [data-counter] tick up from 0 to their final value
     when they enter the viewport. Parses leading number from text content
     so suffixes like "+", " kg/day", "%", etc. are preserved. */
  function animateCounter(el) {
    const original = el.textContent.trim();
    // Pull out the leading numeric portion (handles 1971, 1,250, 14.5, ± 28)
    const match = original.match(/^([±+\-]?\s?[\d.,]+)([\s\S]*)$/);
    if (!match) return;
    const raw = match[1].replace(/[±+\-,\s]/g, '');
    const target = parseFloat(raw);
    const suffix = match[2];
    const prefix = match[1].match(/^[±+\-]?\s?/)[0];
    if (isNaN(target) || target === 0) return;

    // POST-CUSTOMER-AUDIT 2026-05-21 — Skip animation for categorical stats
    // like "16GG", "3GG", "63+", "48hr" where the value is a label, not a
    // count. The 0→target ramp showed nonsense midpoints ("3GG FINEST GAUGE"
    // when the label is supposed to read "16GG is the finest"). Heuristic:
    // suffix begins with a letter/+/% with no leading space, OR target ≤ 9.
    if (target <= 9 || /^[A-Za-z+%]/.test(suffix)) return;

    const duration = Math.min(1000, 500 + Math.log10(Math.max(target, 10)) * 200);
    const start = performance.now();
    const isFloat = match[1].includes('.');
    const decimals = isFloat ? (match[1].split('.')[1] || '').length : 0;

    function step(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const current = target * eased;
      const formatted = isFloat ? current.toFixed(decimals) : Math.round(current).toLocaleString();
      el.textContent = prefix + formatted + suffix;
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = original;
    }

    el.textContent = prefix + (isFloat ? '0' + (decimals ? '.' + '0'.repeat(decimals) : '') : '0') + suffix;
    requestAnimationFrame(step);
  }

  if ('IntersectionObserver' in window) {
    const counterIO = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !entry.target.dataset.counted) {
          entry.target.dataset.counted = '1';
          animateCounter(entry.target);
          counterIO.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    document.querySelectorAll('.stat__number, .prg-stat__number, .team-stat__number, [data-counter]').forEach(function(el) {
      counterIO.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     PDP content-derived tint — Phase 11 (Wave H)
     --------------------------------------------------------------------------
     Sample the dominant non-neutral hue from the product hero image and
     publish it as `--pdp-tint` on <html>. CSS color-mix() then uses that
     value to subtly tint chrome on the product page only. */
  (function pdpTint() {
    if (!document.body.classList.contains('template-page-product-detail') &&
        !document.body.classList.contains('template-page-product')) return;
    const hero = document.querySelector('.pd-gallery img, .pd-hero img, .product__image img, [data-product-hero] img');
    if (!hero) return;
    const apply = function() {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 8; canvas.height = 8;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(hero, 0, 0, 8, 8);
        const data = ctx.getImageData(0, 0, 8, 8).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < data.length; i += 4) {
          const cr = data[i], cg = data[i + 1], cb = data[i + 2];
          // Skip near-neutral pixels (greyscale-ish) so the tint is hue-driven
          if (Math.max(cr, cg, cb) - Math.min(cr, cg, cb) > 24) {
            r += cr; g += cg; b += cb; n++;
          }
        }
        if (n === 0) return;
        r = Math.round(r / n); g = Math.round(g / n); b = Math.round(b / n);
        document.documentElement.style.setProperty('--pdp-tint', `rgb(${r} ${g} ${b})`);
      } catch (e) { /* CORS or other; safe to ignore */ }
    };
    if (hero.complete && hero.naturalHeight !== 0) apply();
    else hero.addEventListener('load', apply, { once: true });
  })();

  /* --------------------------------------------------------------------------
     Quality polish — Phase 12 (Wave H)
     --------------------------------------------------------------------------
     Keyboard shortcuts and global Esc-to-close behavior. */
  document.addEventListener('keydown', function(e) {
    // "/" focuses search (skip when in input/textarea)
    const tag = e.target.tagName;
    const inField = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

    if (e.key === '/' && !inField && !e.metaKey && !e.ctrlKey) {
      const searchTrigger = document.querySelector('[data-search-trigger]');
      const searchInput = document.querySelector('.search-drawer__input');
      if (searchTrigger || searchInput) {
        e.preventDefault();
        if (searchTrigger) searchTrigger.click();
        setTimeout(function() {
          document.querySelector('.search-drawer__input')?.focus();
        }, 60);
      }
    }
    // Cmd+K / Ctrl+K — same as "/"
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      const searchTrigger = document.querySelector('[data-search-trigger]');
      if (searchTrigger) {
        e.preventDefault();
        searchTrigger.click();
        setTimeout(function() {
          document.querySelector('.search-drawer__input')?.focus();
        }, 60);
      }
    }
    // Escape — close any open drawer/menu
    if (e.key === 'Escape') {
      // Close drawers
      document.querySelectorAll('.search-drawer.is-open, .mobile-drawer.is-open, [data-cart-drawer].is-open, [data-wishlist-drawer].is-open, [data-sample-cart-drawer].is-open').forEach(function(el) {
        el.classList.remove('is-open');
      });
      document.querySelectorAll('.mobile-overlay.is-active, [data-cart-overlay].is-active, [data-wishlist-overlay].is-active, [data-sample-cart-overlay].is-active').forEach(function(el) {
        el.classList.remove('is-active');
      });
      // Close any open popovers
      document.querySelectorAll('[popover]').forEach(function(el) {
        if (el.matches(':popover-open')) el.hidePopover();
      });
      document.body.style.overflow = '';
    }
  });

  /* --------------------------------------------------------------------------
     Progressive image reveal — Phase 6
     --------------------------------------------------------------------------
     Mark each lazy-loaded image with [data-loaded] when its `load` event
     fires. CSS handles the fade-in. Also handles the .prog-image wrapper
     pattern (LQIP placeholder + full crossfade). */
  function markLoaded(img) {
    img.setAttribute('data-loaded', '');
    const wrapper = img.closest('[data-prog]');
    if (wrapper) wrapper.setAttribute('data-loaded', '');
  }
  document.querySelectorAll('img[loading="lazy"], img[data-prog-full]').forEach(function(img) {
    if (img.complete && img.naturalHeight !== 0) {
      markLoaded(img);
    } else {
      img.addEventListener('load', function() { markLoaded(img); }, { once: true });
      img.addEventListener('error', function() { markLoaded(img); }, { once: true });
    }
  });

  /* --------------------------------------------------------------------------
     SAMPLE CART — persistent localStorage-backed multi-item sample request
     -------------------------------------------------------------------------- */
  const SAMPLE_CART_KEY = 'bsc-sample-cart-v1';
  const SAMPLE_CART_EVENT = 'bsc:sample-cart:change';

  const SampleCart = {
    /* ---- state ---- */
    _read() {
      try {
        const raw = localStorage.getItem(SAMPLE_CART_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    _write(items) {
      try {
        localStorage.setItem(SAMPLE_CART_KEY, JSON.stringify(items));
      } catch (e) { /* quota or private mode */ }
      window.dispatchEvent(new CustomEvent(SAMPLE_CART_EVENT, { detail: { items } }));
    },

    get() { return this._read(); },

    count() { return this._read().length; },

    has(id) { return this._read().some(i => i.id === id); },

    /* ---- actions ---- */
    add(item) {
      if (!item || !item.id) return false;
      const items = this._read();
      if (items.some(i => i.id === item.id)) return false;
      items.push({
        id: item.id,
        type: item.type || 'item',
        title: item.title || '',
        reference: item.reference || '',
        context: item.context || '',
        image: item.image || '',
        availability: item.availability || '',
        price_label: item.price_label || '',
        added_at: Date.now()
      });
      this._write(items);
      return true;
    },

    remove(id) {
      const items = this._read().filter(i => i.id !== id);
      this._write(items);
    },

    clear() { this._write([]); },

    /* ---- UI render ---- */
    render() {
      this._renderBadge();
      this._renderDrawer();
      this._syncButtons();
    },

    _renderBadge() {
      const n = this.count();
      document.querySelectorAll('[data-sample-cart-count]').forEach(el => {
        el.textContent = n;
        el.closest('[data-sample-cart-trigger]')?.setAttribute('data-count', n);
      });
      const trigger = document.querySelector('[data-sample-cart-trigger]');
      if (trigger) trigger.classList.toggle('is-populated', n > 0);
    },

    _renderDrawer() {
      const list = document.querySelector('[data-sample-cart-list]');
      const empty = document.querySelector('[data-sample-cart-empty]');
      const populated = document.querySelector('[data-sample-cart-populated]');
      if (!list) return;

      const items = this._read();

      // LOCK-SC1 — Toggle clear-all wrapper (header) visibility based on populated state.
      // Hide the wrap entirely when cart is empty + reset any in-flight confirm state.
      // Done up-front so the empty-state early return below doesn't skip it.
      const clearWrap = document.querySelector('[data-sample-cart-clear-wrap]');
      if (clearWrap) {
        clearWrap.hidden = items.length === 0;
        if (items.length === 0) clearWrap.classList.remove('is-confirming');
      }

      if (items.length === 0) {
        list.innerHTML = '';
        if (empty) empty.hidden = false;
        if (populated) populated.hidden = true;
        return;
      }
      if (empty) empty.hidden = true;
      if (populated) populated.hidden = false;

      list.innerHTML = items.map(item => {
        // PHASE-27 D2/D3 — dot + neutral text pattern; data-avail drives the dot color
        const availBadge = item.availability
          ? `<span class="sc-item__avail" data-avail="${this._esc(item.availability)}">
               <span class="sc-item__avail-dot" aria-hidden="true"></span>
               <span class="sc-item__avail-label label-secondary">${this._availLabel(item.availability)}</span>
             </span>`
          : '';
        const ctx = item.context ? `<span class="sc-item__ctx">${this._esc(item.context)}</span>` : '';
        const ref = item.reference ? `<span class="sc-item__ref label-secondary">${this._esc(item.reference)}</span>` : '';
        const price = item.price_label ? `<span class="sc-item__price">${this._esc(item.price_label)}</span>` : '';
        const img = item.image
          ? `<img src="${this._esc(item.image)}" alt="${this._esc(item.title)}" loading="lazy">`
          : `<div class="sc-item__ph"></div>`;
        return `
          <li class="sc-item" data-id="${this._esc(item.id)}">
            <div class="sc-item__image">${img}</div>
            <div class="sc-item__body">
              ${ref}
              <span class="sc-item__title">${this._esc(item.title)}</span>
              ${ctx}
              <div class="sc-item__meta">${availBadge}${price}</div>
            </div>
            <button type="button" class="sc-item__remove" data-sample-cart-remove="${this._esc(item.id)}" aria-label="Remove ${this._esc(item.title)}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </li>
        `;
      }).join('');

      /* LOCK-SC1 clear-wrap toggle moved to the top of _renderDrawer (before the empty-state early return). */
    },

    _syncButtons() {
      const ids = new Set(this._read().map(i => i.id));
      document.querySelectorAll('[data-sample-add]').forEach(btn => {
        const isAdded = ids.has(btn.getAttribute('data-id'));
        if (!btn.getAttribute('data-id')) return;
        btn.classList.toggle('is-added', isAdded);
        btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
        // PHASE-28 D7 — native title attr replaces width-flexing ::after suffix
        if (isAdded) {
          btn.setAttribute('title', 'Tap to remove');
        } else {
          btn.removeAttribute('title');
        }
      });
    },

    /* D24 — aria-live announcement helper. Clear-then-set with 50ms delay
       ensures screen readers re-announce duplicate strings. */
    announce(msg) {
      const el = document.querySelector('[data-sample-cart-announce]');
      if (!el) return;
      el.textContent = '';
      setTimeout(() => { el.textContent = msg; }, 50);
    },

    _availLabel(v) {
      switch (v) {
        case 'in-stock':     return 'In stock';
        case 'limited':      return 'Limited';
        case 'made-to-order':return 'Made to order';
        case 'discontinued': return 'Discontinued';
        default: return v;
      }
    },

    _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  };

  // Expose for other scripts (samples form)
  window.BSCSampleCart = SampleCart;

  /* --------------------------------------------------------------------------
     SAMPLE CART — DOM wiring
     -------------------------------------------------------------------------- */

  // Drawer open/close (PHASE-27 D14 — focus trap + ESC + restore-focus + D25 inert)
  const scDrawer = document.querySelector('[data-sample-cart-drawer]');
  const scOverlay = document.querySelector('[data-sample-cart-overlay]');
  let scLastFocused = null;

  function openSampleCart() {
    if (!scDrawer) return;
    scLastFocused = document.activeElement;
    scDrawer.classList.add('is-open');
    if (scOverlay) scOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    // D25 — set inert on background siblings so they're not tabbable / readable by AT.
    // CRITICAL: skip elements that are INSIDE the drawer itself (the drawer markup
    // uses semantic <header> + <footer> tags that match the selector), and skip
    // ancestors that contain the drawer. Otherwise the drawer's own footer
    // becomes inert and the Review & Submit link can't be clicked.
    document.querySelectorAll('main, header, footer').forEach(el => {
      if (scDrawer.contains(el)) return;       // skip drawer's own <header>/<footer>
      if (el.contains(scDrawer)) return;       // skip any ancestor of drawer (defensive)
      el.setAttribute('inert', '');
    });
    // Focus first interactive element inside the drawer
    const firstFocusable = scDrawer.querySelector('button:not([hidden]), a, input, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      requestAnimationFrame(() => firstFocusable.focus());
    }
  }
  function closeSampleCart() {
    if (!scDrawer) return;
    scDrawer.classList.remove('is-open');
    if (scOverlay) scOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
    // Remove inert from background
    document.querySelectorAll('[inert]').forEach(el => el.removeAttribute('inert'));
    // Restore focus
    if (scLastFocused && typeof scLastFocused.focus === 'function') {
      try { scLastFocused.focus(); } catch (_) { /* element may have been removed */ }
    }
    scLastFocused = null;
  }

  // Click handlers (event delegation so buttons added dynamically still work)
  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-sample-cart-trigger]');
    if (trigger) { e.preventDefault(); openSampleCart(); return; }

    const closeBtn = e.target.closest('[data-sample-cart-close]');
    if (closeBtn) { e.preventDefault(); closeSampleCart(); return; }

    const overlay = e.target.closest('[data-sample-cart-overlay]');
    if (overlay) { closeSampleCart(); return; }

    const addBtn = e.target.closest('[data-sample-add]');
    if (addBtn) {
      e.preventDefault();
      // PHASE-27 D7 — ripple killed; no class-toggle, no scale transform
      const id = addBtn.getAttribute('data-id');
      if (!id) return;
      const title = addBtn.getAttribute('data-title') || 'Item';
      if (SampleCart.has(id)) {
        SampleCart.remove(id);
        SampleCart.announce(title + ' removed.');
      } else {
        SampleCart.add({
          id: id,
          type:         addBtn.getAttribute('data-type') || 'item',
          title:        addBtn.getAttribute('data-title') || '',
          reference:    addBtn.getAttribute('data-reference') || '',
          context:      addBtn.getAttribute('data-context') || '',
          image:        addBtn.getAttribute('data-image') || '',
          availability: addBtn.getAttribute('data-availability') || '',
          price_label:  addBtn.getAttribute('data-price') || ''
        });
        SampleCart.announce(title + ' added to sample cart.');
        /* F4 (2026-05-28): do NOT auto-open the drawer on every add. The drawer
           slides in from the right and overlays adjacent swatch buttons on
           dense grids (Colour Card especially), making it impossible to add a
           second item without first dismissing the drawer (Cowork smoke test).
           The floating sample-cart pill at the bottom-right updates its count
           on every add via the `bsc:sample-cart:change` event, giving the user
           visual feedback. Users open the full drawer when ready by clicking
           the pill. Matches conventional e-commerce UX (Amazon, etc). */
      }
      return;
    }

    const removeBtn = e.target.closest('[data-sample-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const id = removeBtn.getAttribute('data-sample-cart-remove');
      SampleCart.remove(id);
      return;
    }

    // BUGFIX 2026-05-16: replaced two-step confirm with single-click clear.
    // The previous two-step flow (Clear all → Yes/Cancel pair) appeared visually
    // identical to the user — the confirm pair is so subtle in the same header row
    // that users perceived the first click as "nothing happened" or "drawer closed",
    // then 5s auto-revert masked the bug. Sample cart is local-state only and items
    // can be re-added in seconds, so confirmation overhead is unnecessary friction.
    const clearBtn = e.target.closest('[data-sample-cart-clear]');
    if (clearBtn) {
      e.preventDefault();
      SampleCart.clear();
      SampleCart.announce('Sample cart cleared.');
      return;
    }
  });

  // ESC closes drawer; Tab traps focus within drawer when open (D14)
  document.addEventListener('keydown', function(e) {
    if (!scDrawer || !scDrawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      closeSampleCart();
      return;
    }
    if (e.key === 'Tab') {
      const focusables = scDrawer.querySelectorAll(
        'button:not([hidden]):not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Listen to state changes → re-render
  window.addEventListener(SAMPLE_CART_EVENT, function() { SampleCart.render(); });

  // Cross-tab sync — if user has cart open in two tabs, reflect changes
  window.addEventListener('storage', function(e) {
    if (e.key === SAMPLE_CART_KEY) SampleCart.render();
  });

  // Initial render
  document.addEventListener('DOMContentLoaded', function() { SampleCart.render(); });
  // In case DOMContentLoaded already fired
  if (document.readyState !== 'loading') SampleCart.render();

  /* PHASE-PRE-LAUNCH: post-submission cart wipe.
     When the samples form posts successfully, the rendered page contains a
     [data-samples-submitted] marker (see b2b-samples.liquid thank-you state).
     Detect it and clear the persisted cart so the floating pill and review
     panel don't keep showing items the user already requested — prevents
     accidental duplicate-fulfillment submissions. */
  function clearSampleCartOnSuccess() {
    if (document.querySelector('[data-samples-submitted]')) {
      SampleCart.clear();
    }
  }
  document.addEventListener('DOMContentLoaded', clearSampleCartOnSuccess);
  if (document.readyState !== 'loading') clearSampleCartOnSuccess();

  /* Bulk add — "Add entire palette to samples" on seasonal pages */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-ssn-add-all]');
    if (!btn) return;
    e.preventDefault();
    const raw = btn.getAttribute('data-palette-items');
    if (!raw) return;
    let items;
    try { items = JSON.parse(raw); } catch (err) { console.error('Invalid palette JSON:', err); return; }
    if (!Array.isArray(items) || items.length === 0) return;

    let added = 0;
    items.forEach(function(item) {
      if (!SampleCart.has(item.id)) {
        if (SampleCart.add(item)) added++;
      }
    });

    // Open drawer so user sees the result
    if (scDrawer) {
      scDrawer.classList.add('is-open');
      if (scOverlay) scOverlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    // Brief feedback on the button
    const originalText = btn.textContent.trim();
    btn.textContent = added > 0 ? added + ' added to samples ✓' : 'Already in samples';
    btn.setAttribute('disabled', 'disabled');
    setTimeout(function() {
      btn.textContent = originalText;
      btn.removeAttribute('disabled');
    }, 2400);
  });

  /* Spec PDF — now a simple static URL download link, handled by the
     browser via the <a download> attribute. No JS generation needed. */

})();
